import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import confetti from "canvas-confetti";
import {
  X,
  Send,
  Loader2,
  Sparkles,
  Headset,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import AnimatedSoundWave from "@/components/landing/AnimatedSoundWave";
import { summarizeVisitorIntent } from "@/hooks/useVisitorIntent";
import ShareStrip from "@/components/common/ShareStrip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "sb-chat-session-id";

/**
 * fireWaitlistConfetti
 * --------------------
 * Fires a small dual-burst of cyan + magenta confetti aimed at the top
 * of the chat panel — the celebratory moment when Sable confirms the
 * visitor is on the reopening waitlist. Kept short (~1.2s), tasteful,
 * and coloured on-brand so it doesn't feel gimmicky.
 */
const fireWaitlistConfetti = () => {
  try {
    const shared = {
      particleCount: 55,
      spread: 55,
      startVelocity: 32,
      gravity: 0.9,
      ticks: 120,
      scalar: 0.9,
      disableForReducedMotion: true,
    };
    // Anchor the burst near the top-right area where the chat panel
    // lives on desktop (fallback centres on mobile automatically).
    const isDesktop = typeof window !== "undefined" && window.innerWidth >= 768;
    const origin = isDesktop ? { x: 0.85, y: 0.35 } : { x: 0.5, y: 0.4 };
    confetti({
      ...shared,
      angle: 60,
      origin,
      colors: ["#00f0ff", "#a78bfa", "#ffffff"],
    });
    confetti({
      ...shared,
      angle: 120,
      origin,
      colors: ["#ff0055", "#f5c065", "#ffffff"],
    });
  } catch {
    /* confetti is a nice-to-have — silent no-op if the canvas library
       can't init (SSR, blocked, etc.) */
  }
};

// Tiny markdown-ish renderer: converts **bold** and newlines.
const renderMessage = (text) => {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return (
        <strong key={i} className="font-semibold text-white">
          {p.slice(2, -2)}
        </strong>
      );
    }
    return p.split("\n").map((line, j, arr) => (
      <span key={`${i}-${j}`}>
        {line}
        {j < arr.length - 1 && <br />}
      </span>
    ));
  });
};

const FALLBACK_PROMPTS = [
  "Get on the reopening waitlist",
  "Book a mobile shoot",
  "What can you still book?",
];

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState(FALLBACK_PROMPTS);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [escalateForm, setEscalateForm] = useState({
    name: "",
    email: "",
    phone: "",
    note: "",
    website: "", // honeypot
  });
  const [escalateState, setEscalateState] = useState({
    loading: false,
    success: null,
    error: "",
  });
  const escalateOpenedAtRef = useRef(0);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Restore session id on mount
  useEffect(() => {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) setSessionId(cached);
  }, []);

  // Idle fade — after 2.5s without scroll/pointer/touch activity the
  // launcher fades to 25% opacity so it stops competing with content.
  // Any scroll or user interaction wakes it back up. Skipped while the
  // panel is open so the fade doesn't distract from the conversation.
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    if (open) {
      setIdle(false);
      return undefined;
    }
    let timer = null;
    const wake = () => {
      setIdle(false);
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => setIdle(true), 2500);
    };
    // Start already active but schedule the first fade
    wake();
    const events = ["scroll", "wheel", "touchstart", "touchmove", "pointerdown", "pointermove", "keydown"];
    events.forEach((e) => window.addEventListener(e, wake, { passive: true }));
    return () => {
      if (timer) window.clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, wake));
    };
  }, [open]);

  // Auto-scroll on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages, sending, open]);

  // Focus input when opening
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 250);
    }
  }, [open]);

  const ensureSession = async () => {
    if (sessionId) {
      // try to load existing history if not loaded
      if (messages.length === 0) {
        try {
          const { data } = await axios.get(
            `${API}/chat/${sessionId}/history`,
          );
          if (data?.messages?.length) {
            setMessages(
              data.messages.map((m) => ({
                role: m.role,
                content: m.content,
              })),
            );
            return sessionId;
          }
        } catch {
          /* fall through to create new */
        }
      } else {
        return sessionId;
      }
    }
    setBootstrapping(true);
    try {
      const visitor_context = summarizeVisitorIntent();
      const { data } = await axios.post(`${API}/chat/session`, {
        referrer: typeof window !== "undefined" ? window.location.href : "",
        visitor_context,
      });
      setSessionId(data.session_id);
      localStorage.setItem(STORAGE_KEY, data.session_id);
      setMessages([{ role: "assistant", content: data.greeting }]);
      if (Array.isArray(data.suggestions) && data.suggestions.length > 0) {
        setSuggestions(data.suggestions);
      }
      return data.session_id;
    } catch {
      setMessages([
        {
          role: "assistant",
          content:
            "Hmm, the assistant is offline right now. Use the Book Session button or email info@sbmediastudio.com and we'll respond fast.",
        },
      ]);
      return null;
    } finally {
      setBootstrapping(false);
    }
  };

  const handleOpen = async () => {
    setOpen(true);
    if (!sessionId || messages.length === 0) {
      await ensureSession();
    }
  };

  const sendMessage = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || sending) return;
    const sid = sessionId || (await ensureSession());
    if (!sid) return;

    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat/${sid}/message`, {
        message: trimmed,
        visitor_context: summarizeVisitorIntent(),
      });
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.reply },
      ]);
      // Waitlist celebration — Sable just added the visitor to the
      // reopen waitlist, fire a small cyan/pink confetti burst.
      if (data.waitlist_added) {
        fireWaitlistConfetti();
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Something went wrong on my end. Try again, or use Talk to a human to send the team a note.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const handleEscalate = async () => {
    if (!sessionId) return;
    if (!escalateForm.email && !escalateForm.phone) {
      setEscalateState({
        loading: false,
        success: false,
        error: "Add at least an email or a phone so the team can reach you.",
      });
      return;
    }
    setEscalateState({ loading: true, success: null, error: "" });
    try {
      await axios.post(`${API}/chat/${sessionId}/escalate`, {
        ...escalateForm,
        form_ms: escalateOpenedAtRef.current
          ? Date.now() - escalateOpenedAtRef.current
          : undefined,
      });
      setEscalateState({ loading: false, success: true, error: "" });
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Got it — your note and our chat have been sent to the team. They'll reach out by the contact you provided, usually within a few hours.",
        },
      ]);
      setTimeout(() => setEscalateOpen(false), 1400);
    } catch {
      setEscalateState({
        loading: false,
        success: false,
        error: "Couldn't send. Try again in a moment.",
      });
    }
  };

  return (
    <>
      {/* Floating launcher — animated waveform orb with a podcast mic in the center */}
      <motion.button
        type="button"
        onClick={handleOpen}
        data-testid="chat-launcher"
        aria-label="Open chat with Sable"
        initial={{ opacity: 0, scale: 0.5, y: 24 }}
        animate={{
          opacity: open ? 0 : idle ? 0.25 : 1,
          scale: open ? 0.5 : 1,
          y: open ? 24 : 0,
        }}
        transition={{ duration: idle ? 0.6 : 0.35, ease: [0.16, 1, 0.3, 1] }}
        whileHover={{ scale: 1.06, opacity: 1 }}
        whileTap={{ scale: 0.94, opacity: 1 }}
        onFocus={() => setIdle(false)}
        onMouseEnter={() => setIdle(false)}
        className="fixed bottom-5 right-5 md:bottom-7 md:right-7 z-[420] w-20 h-20 md:w-24 md:h-24 rounded-full pointer-events-auto group"
        style={{ pointerEvents: open ? "none" : "auto" }}
      >
            <AnimatedSoundWave
          className="w-full h-full"
          compact
          centerNode={
            <div className="relative">
              <svg
                viewBox="0 0 100 100"
                className="w-8 h-8 md:w-9 md:h-9 drop-shadow-[0_0_16px_#00f0ff]"
                aria-hidden="true"
              >
                {/* mic capsule */}
                <rect
                  x="38"
                  y="18"
                  width="24"
                  height="44"
                  rx="12"
                  ry="12"
                  fill="#ffffff"
                />
                {/* U-shaped yoke */}
                <path
                  d="M28 44 v8 a22 22 0 0 0 44 0 v-8 h-6 v8 a16 16 0 0 1 -32 0 v-8 z"
                  fill="#ffffff"
                />
                {/* stand stem */}
                <rect x="47" y="70" width="6" height="8" fill="#ffffff" />
                {/* base */}
                <rect
                  x="34"
                  y="76"
                  width="32"
                  height="6"
                  rx="2"
                  fill="#ffffff"
                />
              </svg>
              <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#00f0ff] animate-pulse ring-2 ring-[#05050a]" />
            </div>
          }
        />
        {/* subtle hover label */}
        <span className="pointer-events-none absolute right-[110%] top-1/2 -translate-y-1/2 whitespace-nowrap px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md border border-white/10 text-[11px] uppercase tracking-[0.28em] text-white/85 opacity-0 group-hover:opacity-100 transition-opacity">
          Ask Sable
        </span>
      </motion.button>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="chat-panel"
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            data-testid="chat-panel"
            className="fixed inset-x-4 top-4 bottom-4 md:inset-x-auto md:top-auto md:bottom-6 md:right-6 md:left-auto z-[430] md:w-[400px] md:h-[640px] md:max-h-[calc(100vh-3rem)] rounded-3xl overflow-hidden border border-white/10 bg-[#0a0612]/95 backdrop-blur-xl shadow-[0_40px_120px_rgba(255,0,85,0.18)] flex flex-col"
          >
            {/* Header */}
            <div className="relative flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-gradient-to-b from-[#11091a] to-[#0a0612]">
              <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#ff0055]/25 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-16 -left-16 w-44 h-44 rounded-full bg-[#00f0ff]/20 blur-3xl pointer-events-none" />
              <div className="relative w-10 h-10 rounded-2xl bg-gradient-to-br from-[#ff0055] via-[#6b21d9] to-[#00f0ff] flex items-center justify-center shadow-inner">
                <Sparkles size={16} className="text-white" />
              </div>
              <div className="relative flex-1 min-w-0">
                <div className="font-display font-bold text-base leading-tight">
                  Sable
                </div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] mt-0.5 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00f0ff] animate-pulse" />
                  AI concierge · live
                </div>
              </div>
              
              <ShareStrip campaign="sable_chat" title="Chat with Sable — SB Media Studio" text="AI concierge for SB Media Studio. Get pricing + book a session." className="mr-1" />
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="chat-close"
                aria-label="Close chat"
                className="relative w-10 h-10 md:w-9 md:h-9 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 flex items-center justify-center active:scale-95 transition-all"
              >
                <X size={18} className="md:w-4 md:h-4" strokeWidth={2.5} />
              </button>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              data-testid="chat-messages"
              className="flex-1 overflow-y-auto px-4 py-5 space-y-3"
            >
              {bootstrapping && messages.length === 0 && (
                <div className="flex justify-center pt-8">
                  <Loader2 size={20} className="animate-spin text-[#a395c3]" />
                </div>
              )}
              {messages.map((m, i) => (
                <motion.div
                  key={`${m.role}-${i}-${(m.content || "").slice(0, 24)}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className={`flex ${
                    m.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    data-testid={`chat-msg-${m.role}`}
                    className={`max-w-[85%] text-[14px] leading-relaxed rounded-2xl px-4 py-2.5 ${
                      m.role === "user"
                        ? "bg-[#00f0ff] text-[#05050a] font-medium rounded-br-md"
                        : "bg-white/[0.06] border border-white/10 text-white/95 rounded-bl-md"
                    }`}
                  >
                    {renderMessage(m.content)}
                  </div>
                </motion.div>
              ))}
              {sending && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-white/[0.06] border border-white/10 rounded-2xl rounded-bl-md px-4 py-3 inline-flex items-center gap-1.5">
                    {[0, 1, 2].map((d) => (
                      <span
                        key={d}
                        className="w-1.5 h-1.5 rounded-full bg-[#00f0ff] animate-bounce"
                        style={{ animationDelay: `${d * 120}ms` }}
                      />
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Quick prompts (only on first turn) */}
              {messages.length === 1 && !sending && (
                <div className="pt-3 flex flex-wrap gap-2">
                  {suggestions.map((p) => {
                    const isDiscovery = p === "Book a discovery call";
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => {
                          if (isDiscovery) {
                            window.dispatchEvent(
                              new CustomEvent("sb:open-discovery"),
                            );
                          } else {
                            sendMessage(p);
                          }
                        }}
                        data-testid={`chat-quick-${p
                          .toLowerCase()
                          .replace(/\s+/g, "-")
                          .slice(0, 20)}`}
                        className="text-[12px] px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-white/80 hover:bg-[#00f0ff] hover:text-[#05050a] hover:border-[#00f0ff] transition-colors"
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Talk-to-a-human bar */}
            <div className="px-4 py-2 border-t border-white/5 flex items-center justify-between gap-2 bg-black/30">
              <button
                type="button"
                onClick={() => {
                  setEscalateState({ loading: false, success: null, error: "" });
                  escalateOpenedAtRef.current = Date.now();
                  setEscalateOpen(true);
                }}
                data-testid="chat-escalate"
                className="text-[11px] uppercase tracking-[0.22em] text-[#a395c3] hover:text-[#ff0055] inline-flex items-center gap-1.5 transition-colors"
              >
                <Headset size={12} /> Talk to a human
              </button>
              <span className="text-[10px] uppercase tracking-[0.22em] text-white/30">
                Powered by Claude
              </span>
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                sendMessage();
              }}
              className="px-3 py-3 border-t border-white/10 bg-[#0a0612] flex items-center gap-2"
            >
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about pricing, packages, gear…"
                data-testid="chat-input"
                disabled={sending}
                className="flex-1 bg-white/[0.04] border border-white/10 rounded-full px-4 py-2.5 text-[14px] text-white placeholder:text-white/30 focus:outline-none focus:border-[#00f0ff]/50 focus:bg-white/[0.06] transition-colors disabled:opacity-50"
                maxLength={3500}
              />
              <button
                type="submit"
                data-testid="chat-send"
                disabled={sending || !input.trim()}
                className="w-10 h-10 rounded-full bg-[#ff0055] text-white flex items-center justify-center hover:bg-[#00f0ff] hover:text-[#05050a] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                aria-label="Send"
              >
                {sending ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <Send size={15} />
                )}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Escalate / contact form modal */}
      <AnimatePresence>
        {escalateOpen && (
          <motion.div
            key="esc"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setEscalateOpen(false)}
            data-testid="chat-escalate-modal"
            className="fixed inset-0 z-[440] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4"
          >
            <motion.div
              initial={{ y: 20, scale: 0.96, opacity: 0 }}
              animate={{ y: 0, scale: 1, opacity: 1 }}
              exit={{ y: 20, scale: 0.96, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md rounded-3xl bg-[#0a0612] border border-white/10 p-6 md:p-7 shadow-[0_30px_120px_rgba(255,0,85,0.2)]"
            >
              <button
                type="button"
                onClick={() => setEscalateOpen(false)}
                aria-label="Close"
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/[0.06] border border-white/10 flex items-center justify-center hover:bg-white/[0.12]"
              >
                <X size={14} />
              </button>
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#ff0055] mb-2">
                [ Talk to a human ]
              </div>
              <h3 className="font-display font-black text-2xl tracking-tight">
                Send your chat to the team.
              </h3>
              <p className="text-[#a395c3] text-sm mt-1.5">
                Drop your contact and an optional note. The team gets the full
                transcript + your details and follows up — usually within a
                few hours.
              </p>

              {escalateState.success ? (
                <div
                  data-testid="chat-escalate-success"
                  className="mt-6 rounded-2xl border border-[#00f0ff]/30 bg-[#00f0ff]/8 p-5 flex items-start gap-3"
                >
                  <CheckCircle2
                    size={20}
                    className="text-[#00f0ff] flex-shrink-0 mt-0.5"
                  />
                  <div>
                    <div className="font-semibold text-white">Sent.</div>
                    <div className="text-sm text-[#a395c3] mt-1">
                      The team will reach out using the contact you provided.
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-5 space-y-3">
                  {/* Honeypot */}
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={escalateForm.website}
                    onChange={(e) =>
                      setEscalateForm({
                        ...escalateForm,
                        website: e.target.value,
                      })
                    }
                    aria-hidden="true"
                    className="absolute left-[-9999px] w-px h-px opacity-0 pointer-events-none"
                  />
                  <input
                    type="text"
                    placeholder="Your name"
                    value={escalateForm.name}
                    onChange={(e) =>
                      setEscalateForm({ ...escalateForm, name: e.target.value })
                    }
                    data-testid="chat-escalate-name"
                    className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-[14px] text-white placeholder:text-white/30 focus:outline-none focus:border-[#00f0ff]/50"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={escalateForm.email}
                    onChange={(e) =>
                      setEscalateForm({
                        ...escalateForm,
                        email: e.target.value,
                      })
                    }
                    data-testid="chat-escalate-email"
                    className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-[14px] text-white placeholder:text-white/30 focus:outline-none focus:border-[#00f0ff]/50"
                  />
                  <input
                    type="tel"
                    placeholder="Phone (optional)"
                    value={escalateForm.phone}
                    onChange={(e) =>
                      setEscalateForm({
                        ...escalateForm,
                        phone: e.target.value,
                      })
                    }
                    data-testid="chat-escalate-phone"
                    className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-[14px] text-white placeholder:text-white/30 focus:outline-none focus:border-[#00f0ff]/50"
                  />
                  <textarea
                    placeholder="What do you want to ask? (optional)"
                    value={escalateForm.note}
                    onChange={(e) =>
                      setEscalateForm({
                        ...escalateForm,
                        note: e.target.value,
                      })
                    }
                    data-testid="chat-escalate-note"
                    rows={3}
                    className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-[14px] text-white placeholder:text-white/30 focus:outline-none focus:border-[#00f0ff]/50 resize-none"
                  />
                  {escalateState.error && (
                    <div
                      data-testid="chat-escalate-error"
                      className="flex items-start gap-2 text-[12px] text-[#ff8da0]"
                    >
                      <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                      <span>{escalateState.error}</span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleEscalate}
                    disabled={escalateState.loading}
                    data-testid="chat-escalate-submit"
                    className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#ff0055] text-white font-semibold text-sm hover:bg-[#00f0ff] hover:text-[#05050a] disabled:opacity-50 transition-colors"
                  >
                    {escalateState.loading ? (
                      <>
                        <Loader2 size={15} className="animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        <Send size={14} /> Send to the team
                      </>
                    )}
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
